import { FormEvent, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { LanguageSelect } from "../shared";
import {
  getStoredGuestLanguage,
  setStoredGuestLanguage,
} from "../../utils/i18n/languagePreference";
import {
  DEFAULT_LANGUAGE,
  canonicalizeLanguageTag,
} from "../../utils/i18n/supportedLanguages";
import {
  REGULATOR_PORTAL_DASHBOARD_ROUTE,
  REGULATOR_PORTAL_SET_PASSWORD_ROUTE,
} from "./regulatorPortalRoutes";
import {
  signInRegulator,
  type RegulatorAuthData,
} from "../../queries/regulator-portal/regulatorAuthClient";
import {
  AUTH_RESPONSE_TYPES,
  classifyAuthResponse,
} from "../../utils/auth/authFlow";
import {
  clearRegulatorPortalSession,
  hasRegulatorPortalSession,
  setRegulatorPortalToken,
} from "../../utils/tokenUtils";

import styles from "./RegulatorPortalSignIn.module.css";

const clearOtherSessions = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("email");
  localStorage.removeItem("lastLogInTime");
  localStorage.removeItem("affiliate_portal_token");
  localStorage.removeItem("affiliate_portal_lastLogInTime");
};

const RegulatorPortalSignIn = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const activeLanguage =
    canonicalizeLanguageTag(getStoredGuestLanguage()) ?? DEFAULT_LANGUAGE;

  const handleLanguageChange = (lng: string) => {
    if (!lng || lng === activeLanguage) return;
    setStoredGuestLanguage(lng);
    window.location.reload();
  };

  // Presence-only would bounce an expired leftover into the dashboard guard.
  if (hasRegulatorPortalSession()) {
    return <Navigate to={REGULATOR_PORTAL_DASHBOARD_ROUTE} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const data = await signInRegulator({
        user_name: email,
        password,
      });
      const result = classifyAuthResponse(data);

      if (result.type === AUTH_RESPONSE_TYPES.NEW_PASSWORD_REQUIRED) {
        navigate(REGULATOR_PORTAL_SET_PASSWORD_ROUTE, {
          state: { username: email, sessionToken: result.sessionToken },
        });
        return;
      }

      if (result.type !== AUTH_RESPONSE_TYPES.FINAL_TOKENS) {
        setErrorMessage(AppStrings.PAM_GENERIC_REQUEST_FAILED);
        return;
      }

      const token = result.token as RegulatorAuthData;
      clearOtherSessions();
      clearRegulatorPortalSession();
      setRegulatorPortalToken(token);
      navigate(REGULATOR_PORTAL_DASHBOARD_ROUTE, { replace: true });
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className={styles.screen} data-testid="regulator-portal-sign-in">
      <form className={styles.card} onSubmit={handleSubmit}>
        <div
          className={styles.languageRow}
          data-testid="regulator-portal-sign-in-language"
        >
          <LanguageSelect
            value={activeLanguage}
            onChange={handleLanguageChange}
          />
        </div>
        <h1 className={styles.title}>{AppStrings.PAM_REGULATOR_PORTAL}</h1>
        <p className={styles.subtitle}>{AppStrings.PAM_REGULATOR_PORTAL_SIGN_IN_SUBTITLE}</p>
        <div className={styles.field}>
          <label htmlFor="regulator-email">{AppStrings.EMAIL}</label>
          <input
            id="regulator-email"
            className={styles.input}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="regulator-password">{AppStrings.PASSWORD}</label>
          <input
            id="regulator-password"
            className={styles.input}
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
        </div>
        {errorMessage && (
          <p className={styles.error} role="alert">
            {errorMessage}
          </p>
        )}
        <button
          className={styles.button}
          type="submit"
          disabled={isSubmitting || !email || !password}
        >
          {isSubmitting ? AppStrings.SAVING : AppStrings.LOG_IN}
        </button>
      </form>
    </main>
  );
};

export default RegulatorPortalSignIn;
